'use client';

import { useEffect } from 'react';
import styled from '@emotion/styled';
import { secondsToMinutes } from 'date-fns';
import { ContentWrap, Responsive } from '@/ui';
import { ISecurityCheckpoint } from '../domain/models/ISecurityCheckpoint';
import WidgetFooter from '../../../components/common/widget/WidgetFooter';
import {
	InfoBlock,
	ParkingWidgetContainer,
	WidgetContent,
	WidgetContentBlock,
	WidgetErrorMessageWrap,
	WidgetTitle,
} from '../../parking-availability/ui/ParkingAvailabilityWidget';
import { Color } from '../../../styles/Color';
import TsaPre from '../../../assets/security/tsa-pre.png';
import TsaPre2 from '../../../assets/security/tsa-pre@2x.png';
import TsaPre3 from '../../../assets/security/tsa-pre@3x.png';
import { capitalizeFirstChar } from '../../../utils/capitalizeFirstChar';
import { ISecurityCheckpointResponse } from '../domain/models/ISecurityCheckpointResponse';
import { SECURITY_API } from '../domain/api';
import ErrorMessage from '../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../utils/errorMessages';
import SecurityWaitTimesWidgetShimmer from './SecurityWaitTimesWidgetShimmer';
import { useGetApiData } from '../../../data/hooks/useGetApiData';

interface ISecurityWaitTimesWidgetProps {
	onError?: () => void;
}

export default function SecurityWaitTimesWidget({
	onError,
}: ISecurityWaitTimesWidgetProps) {
	const { data, loading, error, refetch } =
		useGetApiData<ISecurityCheckpointResponse>(SECURITY_API);

	useEffect(() => {
		if (error) {
			onError?.();
		}
	}, [error]);

	const securityData: { [key: string]: number } = {
		CVGPRE: 1,
		CVGGEN: 2,
	};

	if (loading && !error) {
		return <SecurityWaitTimesWidgetShimmer />;
	}

	if (error || !data) {
		return (
			<WidgetErrorMessageWrap>
				<ErrorMessage message={ErrorMessages.GenericError} />
			</WidgetErrorMessageWrap>
		);
	}

	const sortedSecurityTimes = data?.data.checkpoints.slice().sort((a, b) => {
		const orderA = securityData[a.id];
		const orderB = securityData[b.id];
		return orderA - orderB;
	});

	return (
		<ContentWrap>
			<SecurityWidgetContainer>
				<WidgetHeader>
					<WidgetTitle>Security Wait Times</WidgetTitle>
					Open from 3:30AM - 9:20PM
				</WidgetHeader>
				<WidgetContent>
					{sortedSecurityTimes?.map((item: ISecurityCheckpoint) => (
						<WidgetContentBlock key={item.id}>
							{item.id === 'CVGPRE' ? (
								<img
									src={TsaPre}
									srcSet={`${TsaPre2} 2x, ${TsaPre3} 3x`}
									alt="TSA Pre security checkpoint"
									aria-label="TSA Pre security checkpoint"
									height="30px"
									width="85px"
								/>
							) : (
								capitalizeFirstChar(item.attributes[0])
							)}
							<StyledInfoBlock
								backgroundColor={
									!item.waitSeconds ? Color.Neutral94 : Color.PrimaryBlue87
								}
								color={Color.PrimaryBlue17}
							>
								{item.waitSeconds
									? `${secondsToMinutes(item.waitSeconds)} min`
									: `No info`}
							</StyledInfoBlock>
						</WidgetContentBlock>
					))}
				</WidgetContent>
			</SecurityWidgetContainer>
			<WidgetFooter refetch={refetch} label="Refresh Wait Times" />
		</ContentWrap>
	);
}

export const SecurityWidgetContainer = styled(ParkingWidgetContainer)`
	max-width: 50%;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		max-width: 100%;
	}
`;

const StyledInfoBlock = styled(InfoBlock)`
	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		justify-content: center;
	}
`;

const WidgetHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
		align-items: normal;
	}
`;
